import { useCallback, useState } from 'react';
import pick from 'lodash/pick';
import moment from 'moment';
import TTSRequest from '../../services/TTSRequest';
import TTSRequestManager from '../../services/TTSRequestManager';
import { ClipObject, ClipObjectPending } from '.';
import { useProductUsageQuery } from '../../components/ProductUsage/operation.generated';
import generateSequence from '../../util/generateSequence';
import useStudioClips from '../../components/StudioClipUserList/hooks/useStudioClips';
import { UseTTSInputs } from './useTTS';
import { captureExceptionWithState, isClientError } from '../../util/sentry';
import { EventName, useEventsContext } from '../events/EventsContext';

export type PreviewClip = ClipObject | ClipObjectPending | undefined;
type usePreviewTTSAction = (inputs: UseTTSInputs) => Promise<any>;
type usePreviewResetAction = () => void;

const createPendingClip = (
  inputs: UseTTSInputs,
  lastClipIndex: number,
  stream: any,
  isCreditableBy?: string
): ClipObjectPending => {
  return {
    id: inputs.clipId,
    projectId: inputs.projectId,
    actorId: inputs.actor.id.toString(),
    actor: {
      __typename: 'ClipActor',
      ...pick(inputs.actor, [
        'id',
        'name',
        'image',
        'initials',
        'isCustomActor',
      ]),
      variant: inputs.actor.variant,
    },
    createdAt: moment().valueOf().toString(),
    isCreditableBy,
    streamRequest: stream,
    name: '',
    fileName: '',
    text: inputs.textInput,
    textFormat: inputs.textFormat ?? null,
    isComplete: false,
    index: lastClipIndex + 1 + (inputs.indexOffset || 0),
    sequence: generateSequence(),
    isDeleted: false,
    ttsVersion: inputs.ttsVersion,
  };
};

const usePreviewTTS = (
  projectId: string
): [{ clip: PreviewClip }, usePreviewTTSAction, usePreviewResetAction] => {
  const [clip, setClip] = useState<ClipObject | ClipObjectPending>();
  const { data: usageData } = useProductUsageQuery();
  const [{ clips }] = useStudioClips(projectId);
  const [eventTracking] = useEventsContext();

  const ttsAction = useCallback(
    (inputs: UseTTSInputs) => {
      return new Promise<void>((resolve, reject) => {
        const request = new TTSRequest({
          clipId: inputs.clipId,
          projectId: inputs.projectId,
          actor: {
            id: inputs.actor.id,
            variantId: inputs.actor.variant.id,
            version: inputs.ttsVersion ?? inputs.actor.variant.version,
            initials: inputs.actor.initials,
          },
          text: inputs.textInput,
          textFormat: inputs.textFormat,
          isPreview: true,
        });

        let lastClipIndex = 0;
        if (clips.length > 0) {
          lastClipIndex = clips.sort((a, b) => (a.index > b.index ? 1 : -1))[
            clips.length - 1
          ].index;
        }

        const isCreditableBy: string | undefined =
          usageData?.user &&
          moment
            .unix(usageData.user.productAccessPeriodUsage.periodEnd)
            .valueOf()
            .toString();

        const initialClip = createPendingClip(
          inputs,
          lastClipIndex,
          request.stream,
          isCreditableBy
        );
        let source: string = '';

        setClip(initialClip);
        let updatedClip = initialClip;
        request.on('headersReceived', event => {
          const detail = {
            ...event.detail,
            createdAt: event.detail.createdAt.toISOString(),
          };
          source = event.detail.source;
          setClip({ ...clip, ...detail } as ClipObject);
          updatedClip = { ...updatedClip, ...detail };
          setClip(updatedClip);
          resolve();
        });
        request.on('complete', event => {
          // Just in case we can't read the latest clip value,
          // we must make sure we have the file name for clip
          const fileName = request.stream.getResponseHeader(
            'X-Clip-File-Name'
          ) as string;
          updatedClip = {
            ...updatedClip,
            isComplete: true,
            localSrc: request.stream.getObjectURL(),
            fileName,
          } as ClipObject;
          setClip(updatedClip);
          eventTracking.track({
            type: EventName.ClipPreview,
            data: {
              actorID: `${inputs.actor.id}`,
              actorVariantID: `${inputs.actor.variant.id}`,
              source,
              actorVariantType: inputs.actor.variant.variantType.id,
              actorName: inputs.actor.name,
              textLength: inputs.textInput.length,
              ttsVersion: inputs.ttsVersion ?? inputs.actor.variant.version,
              renderMode: inputs.renderMode,
              success: true,
              timeUntilHeadersReceived: request.timing.headersReceived,
              timeUntilRequestComplete: request.timing.requestComplete,
              cues: inputs.cues,
            },
          });
        });
        request.on('error', event => {
          updatedClip = {
            ...updatedClip,
            failedWithError: event.detail.message,
          };
          setClip(updatedClip);

          eventTracking.track({
            type: EventName.ClipPreview,
            data: {
              actorID: `${inputs.actor.id}`,
              actorVariantID: `${inputs.actor.variant.id}`,
              source,
              actorVariantType: inputs.actor.variant.variantType.id,
              actorName: inputs.actor.name,
              textLength: inputs.textInput.length,
              ttsVersion: inputs.ttsVersion ?? inputs.actor.variant.version,
              renderMode: inputs.renderMode,
              success: false,
              timeUntilHeadersReceived: request.timing.headersReceived,
              timeUntilRequestComplete: request.timing.requestComplete,
              cues: inputs.cues,
            },
          });
          if (!isClientError(request.stream.status)) {
            captureExceptionWithState(new Error(event.detail.message), {
              type: 'Preview Clip',
              value: {
                actorId: `${inputs.actor.id}`,
                actorName: inputs.actor.name,
                textLength: inputs.textInput.length,
              },
            });
          }
          reject(event.detail.message);
        });
        TTSRequestManager.addRequest(request);
      });
    },
    [clip, clips, eventTracking, usageData]
  );

  const resetPreviewClip = useCallback(() => {
    // reset clip in order to create another preview to add to list
    setClip(undefined);
  }, []);
  return [{ clip }, ttsAction, resetPreviewClip];
};

export default usePreviewTTS;
